import { Navigate, useLocation } from 'react-router-dom';
import { AimsLogo } from '@aimsos/ui';
import useAuth from './useAuth.js';

/**
 * Route guard that redirects unauthenticated users to the login page.
 *
 * Works in both standalone and shell-embedded scenarios via useAuth().
 * In embedded mode, the Shell guarantees authentication before mounting
 * child apps, so this guard is always satisfied immediately.
 *
 * The attempted path travels along as `?next=`. Without it the deep link is
 * lost: this redirect replaces the URL with `/login`, and every later step —
 * `LoginPage` stashing `returnPath`, `WorkspaceGate` handing the token to the
 * subdomain — reads `window.location`, which by then says `/login`. The user
 * ends up on the default landing page instead of the page they asked for.
 *
 * @param {object}  props
 * @param {string}  [props.loginPath='/login'] - Path to redirect unauthenticated users to
 * @param {React.ReactNode} [props.loadingFallback] - Custom loading UI
 *
 * @example
 * <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
 */
const ProtectedRoute = ({ children, loginPath = '/login', loadingFallback = null }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      loadingFallback || (
        <div className="relative flex min-h-screen items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-purple-950 to-teal-950" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/40 via-transparent to-transparent" />
          <div className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-purple-600/20 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 h-80 w-80 rounded-full bg-teal-600/20 blur-3xl" />
          <div className="relative z-10 flex flex-col items-center gap-6">
            <AimsLogo size={64} className="drop-shadow-2xl" />
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              <span className="text-sm font-medium text-white/70">Authenticating…</span>
            </div>
          </div>
        </div>
      )
    );
  }

  if (!isAuthenticated) {
    const attempted = `${location.pathname}${location.search}`;
    // `/` and the login page itself carry no intent worth preserving.
    const shouldCarry = attempted !== '/' && !attempted.startsWith(loginPath);
    const to = shouldCarry
      ? `${loginPath}?next=${encodeURIComponent(attempted)}`
      : loginPath;
    return <Navigate to={to} replace />;
  }

  return children;
};

export default ProtectedRoute;
